import { useCallback, useRef, useState } from 'react';

// "Seen" is tracked by server `seq` (a monotonic per-room counter), not by
// array length or message id, and persisted to localStorage per room —
// unlike a plain in-memory ref, this survives the component being torn down
// and recreated, which happens both when the player leaves and re-opens the
// chat surface and when a round transition remounts the whole game screen
// (OnlinePlayScreen.jsx re-keys its child on every round). Without this, a
// full remount always started `seenCount` back at 0 and every backfilled
// message reappeared as unread.
function lastSeenStorageKey(roomId) {
    return `online_chat_last_seen:${roomId}`;
}

function getStoredLastSeenSeq(roomId) {
    if (!roomId) return 0;
    try {
        const n = parseInt(localStorage.getItem(lastSeenStorageKey(roomId)), 10);
        return Number.isFinite(n) ? n : 0;
    } catch {
        return 0;
    }
}

function storeLastSeenSeq(roomId, seq) {
    if (!roomId) return;
    try {
        localStorage.setItem(lastSeenStorageKey(roomId), String(seq));
    } catch {
        // Storage unavailable (private mode, quota) — unread count just won't survive a remount this session.
    }
}

/**
 * Open/closed state plus unread badge count for the in-game chat, shared
 * between the toggle button (now in Header) and the floating panel it opens
 * (ChatPanel.jsx) — moved here so both can be driven from the same state.
 */
export default function useChatUnread(roomId, messages) {
    const [expanded, setExpanded] = useState(false);
    const lastSeenSeqRef = useRef(getStoredLastSeenSeq(roomId));

    const unreadCount = expanded
        ? 0
        : messages.reduce((count, m) => (typeof m.seq === 'number' && m.seq > lastSeenSeqRef.current ? count + 1 : count), 0);

    // Snapshot on every toggle, not just on open: while the panel is open the
    // user is watching messages arrive live, so closing it should start the
    // unread count from "now", not from whatever it was when last opened.
    const toggle = useCallback(() => {
        const maxSeq = messages.reduce((max, m) => (typeof m.seq === 'number' ? Math.max(max, m.seq) : max), lastSeenSeqRef.current);
        lastSeenSeqRef.current = maxSeq;
        storeLastSeenSeq(roomId, maxSeq);
        setExpanded((prev) => !prev);
    }, [messages, roomId]);

    return { expanded, unreadCount, toggle };
}
